import React, { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import classNames from "classnames";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import { ChevronUpOutlined } from "@toddle-edu/ds-icons";

import { getJobFeedQuery } from "Integrations/modules/IntegrationsQueries";
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import { useHandleScroll } from "Utils/CustomHooks";
import { prepareLoadMoreFeed, onEnterOrSpaceKeyDown } from "Utils";
import { getWorkflowItemTitle } from "./modules/Utils";

import AnimateHeight from "UIComponents/AnimateHeight";
import I18nHOC from "UIComponents/I18nHOC";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import WorkflowItem from "./components/WorkflowItem";

import classes from "./WorkflowProgressMenu.scss";

const formatSubtasks = ({ subtasks, t }) => {
  const completedCount = subtasks.filter(
    ({ node: { status } }) => status === "COMPLETED"
  ).length;
  const failedCount = subtasks.filter(
    ({ node: { status } }) => status === "FAILED"
  ).length;

  const formattedSubtasks = subtasks.map(({ node }) => {
    const {
      id,
      status,
      parsedData: {
        workflowTaskOrgLevelLocaleKey = "workflow",
        workflowTaskOrgLevelLocaleParams = {},
        retryAllowed = false,
        numOfRetriesAllowed = 0,
        workflowSubTaskMetadata = {},
        entityType,
      } = {},
    } = node;

    return {
      id,
      title: getWorkflowItemTitle({
        t,
        locale: workflowTaskOrgLevelLocaleKey,
        localeParams: workflowTaskOrgLevelLocaleParams,
      }),
      status,
      retryAllowed,
      numOfRetriesAllowed,
      workflowSubTaskMetadata,
      entityType,
    };
  });

  return {
    subtasks: formattedSubtasks,
    totalCount: subtasks.length,
    completedCount,
    failedCount,
  };
};

const formatJobData = ({ jobs, t }) =>
  jobs.map(({ node }) => {
    const {
      id,
      metadata: {
        workflowLevelLocaleKey = "workflow",
        workflowLevelLocaleParams = {},
      } = {},
    } = node;

    return {
      id,
      title: getWorkflowItemTitle({
        t,
        locale: workflowLevelLocaleKey,
        localeParams: workflowLevelLocaleParams,
      }),
      ...formatSubtasks({ subtasks: _.get(node, "subTasksFeed.edges", []), t }),
    };
  });

const WorkflowItemContainer = ({
  jobs,
  expandedJobId,
  updateExpandedJobId,
  t,
  jobFeedVariables,
}) =>
  formatJobData({ jobs, t }).map(job => (
    <WorkflowItem
      item={job}
      key={job.id}
      expandedJobId={expandedJobId}
      updateExpandedJobId={updateExpandedJobId}
      jobFeedVariables={jobFeedVariables}
    />
  ));

const WorkflowProgressMenu = ({
  t,
  jobs,
  loadMoreFeed,
  hasNextPage,
  networkStatus,
  jobFeedVariables,
}) => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(true);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const scrollContainerRef = useRef(null);

  const toggleIsMenuExpanded = () => {
    setExpandedJobId(null);
    setIsMenuExpanded(prevState => !prevState);
  };

  const updateExpandedJobId = id =>
    setExpandedJobId(prevId => (prevId === id ? null : id));

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: useCallback(() => scrollContainerRef.current, []),
    hookDependencies: [scrollContainerRef.current],
  });

  useEffect(() => {
    setExpandedJobId(jobs?.[0]?.node?.id || null);
  }, [jobs]);

  return jobs.length ? (
    <div className={classes.container}>
      <div
        className={classes.headerContainer}
        onClick={toggleIsMenuExpanded}
        onKeyDown={e => onEnterOrSpaceKeyDown(e, toggleIsMenuExpanded)}
        role="button"
        tabIndex={0}
        aria-expanded={isMenuExpanded}
        aria-label={t("workflows:sync_in_progress_with_count", {
          count: jobs.length,
        })}
      >
        <div className={classes.headerText}>
          {t("workflows:sync_in_progress_with_count", {
            count: jobs.length,
          })}
        </div>
        <ChevronUpOutlined
          variant="subtle"
          size="xxx-small"
          className={classNames(classes.arrowContainer, {
            [classes.openArrow]: isMenuExpanded,
            [classes.closeArrow]: !isMenuExpanded,
          })}
        />
      </div>
      <AnimateHeight duration={300} height={!isMenuExpanded ? 0 : "auto"}>
        <div className={classes.scrollContainer} ref={scrollContainerRef}>
          <WorkflowItemContainer
            t={t}
            jobs={jobs}
            expandedJobId={expandedJobId}
            updateExpandedJobId={updateExpandedJobId}
            jobFeedVariables={jobFeedVariables}
          />
        </div>
      </AnimateHeight>
    </div>
  ) : null;
};

export const getFilterVariablesMemoized = _.memoize(
  ({ organizationId, curriculumProgramId }) => {
    const variables = {
      fetchSubTasksCount: true,
      fetchSubtaskEdges: true,
      id: organizationId,
      filters: { jobType: ["WORKFLOW_JOB"], status: "PROCESSING" },
      first: 25,
    };

    if (!_.isNil(curriculumProgramId)) {
      variables.curriculumProgramId = curriculumProgramId;
    }

    return variables;
  },
  ({ organizationId, curriculumProgramId }) =>
    `${organizationId}_${curriculumProgramId}`
);

const mapStateToProps = state => {
  const organizationId = state.login.userInfo.org_id;
  const curriculumProgramId = getSelectedCurriculumId(state);
  return {
    jobFeedVariables: getFilterVariablesMemoized({
      organizationId,
      curriculumProgramId,
    }),
    orgIds: [organizationId],
  };
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, null),
  graphql(getJobFeedQuery, {
    name: "getJobFeed",
    options: ({ jobFeedVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: jobFeedVariables,
    }),
    props: ({ getJobFeed, ownProps: { jobFeedVariables } }) => {
      const jobs = _.get(getJobFeed, "node", {});
      const pageInfo = _.get(getJobFeed, "node.jobFeed.pageInfo", {});

      return {
        getJobFeed,
        jobs: _.get(jobs, "jobFeed.edges", []),
        onPusherEventTriggered: getJobFeed.refetch,
        isData: !_.isEmpty(jobs),
        isLoading: [1, 2].includes(getJobFeed.networkStatus),
        isMoreJobsLoading: getJobFeed.networkStatus === 3,
        hasNextPage: pageInfo.hasNextPage || false,
        networkStatus: getJobFeed.networkStatus,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            ...jobFeedVariables,
            afterV2: pageInfo.endCursor || null,
          },
          queryData: getJobFeed,
          key: "jobFeed",
        }),
      };
    },
  }),
  withPusherBind([
    "workflowProgressStarted",
    "workflowJobCompleted",
    "workflowProgressUpdate",
  ])
)(WorkflowProgressMenu);
